'use client';

import { c as _c } from "react/compiler-runtime";
import './HomeClient.css';
import { useMapPin } from '@/shared/hooks/useMapPin.mjs';
import ComponentErrorBoundary from '@/shared/components/ComponentErrorBoundary';
import Galaxy from '@/features/galaxy/Galaxy';
import DashboardClient from '@/features/dashboard/DashboardClient';
import EventLog from '@/features/timeline/EventLog';
import NextWaveCard from '@/features/dashboard/NextWaveCard';
import { waveForecast } from '@/features/dashboard/waveForecast.mjs';
import { counterattackForecast } from '@/features/dashboard/counterattackForecast.mjs';
import { buildIntroMarkers } from '@/features/timeline/buildIntroMarkers.mjs';
import { useLiveDataContext } from '@/shared/providers/LiveDataContext.mjs';
import { useScrollEvent } from '@/shared/hooks/useScrollEvent.mjs';
import { eventKey } from '@/shared/utils/game/eventKey.mjs';
import { computeMapStateAtEvent } from '@/shared/utils/game/computeMapStateAtEvent.mjs';
import { computePulseDelays } from '@/shared/utils/game/pulseDelays.mjs';
import { useHeaderGlassFilter } from '@/shared/hooks/useHeaderGlassFilter.mjs';

/**
 * Homepage client — owns a two-column grid: one sidebar that stacks
 * the dashboard (season heading, region cards, stats) and the event
 * log, and a single sticky galaxy map column that can be pinned
 * (mobile/tablet) or lives in its own grid cell (desktop).
 *
 * Layout:
 *
 *   Desktop (lg+):               Mobile/Tablet (<lg, unpinned):
 *   ┌──────────────┬─────────┐   ┌──────────────────┐
 *   │   sidebar    │         │   │    ┌────────┐    │  map in normal
 *   │  (dashboard  │   map   │   │    │  map   │    │  flow, scrolls
 *   │  + event log)│ sticky  │   │    └────────┘    │  away
 *   └──────────────┴─────────┘   ├──────────────────┤
 *     map sticky at                │   sidebar        │
 *     top:80px in                  │   (dashboard     │
 *     right column                 │   + event log)   │
 *                                  └──────────────────┘
 *
 *   Mobile/Tablet (<lg, pinned via FAB):
 *   ┌──────────────────┐
 *   │  map (sticky,    │  ← `.home-map--sticky`, full-bleed bg,
 *   │  at top:49/79px) │    1px overlap with header bottom border,
 *   ├──────────────────┤    z-index:50 above header
 *   │   hero sidebar   │
 *   │   event log      │
 *   └──────────────────┘
 *
 * ## Map state
 *
 * The map's `mapState` source switches based on whether `useScrollEvent`
 * has latched onto an event:
 *   - `selectedEvent` present → time-travel via `computeMapStateAtEvent`
 *     (same mechanic `ArchiveMap` uses on /archives)
 *   - null → live `mapState` from `useLiveDataContext`
 *
 * ## Pin state machine (mobile/tablet only)
 *
 * Two pieces of React state drive three CSS modifier classes:
 *
 *   `isMapSticky` (boolean, default false on /):
 *     - Controls the persistent `.home-map--sticky` class
 *     - When true: `position: sticky; top: 49/79px`, background,
 *       border, full-bleed margins, `--header-offset` transform
 *       tracking, and descendant `#map > svg` max-height cap
 *     - Flipped by the FAB click (`togglePin`)
 *
 *   `isAnimating` (boolean, default false, timer-gated):
 *     - Controls the transient `.home-map--pinning` class for
 *       exactly 400ms after `togglePin` flips `isMapSticky` from
 *       `false` → `true` (not on unpin, not on first mount)
 *     - When true: drops `z-index` to 10 so the header's `z-40`
 *       occludes the map during the slide animation, and applies
 *       the `home-map-pin-in` keyframe
 *
 * The split keeps the slide animation from re-triggering on mount
 * (important for /archives where `isMapSticky` defaults to true) —
 * only an explicit user toggle fires the animation.
 *
 * ## Background mirror on tablet (md+)
 *
 * `useHeaderGlassFilter` reads the `--header-glass-filter` CSS var
 * published by `public/scripts/headerGPU.js` and applies
 * `backdrop-filter: blur(8.8px)` as an inline `style={{}}` on the
 * map element. Inline is required because Lightning CSS strips
 * `backdrop-filter` declarations from the built CSS — see the hook
 * file and `CHANGELOG.md#0.39.14` for the full story. The matching
 * `background` uses `var(--header-bg)` directly from CSS (surviving
 * Lightning CSS's optimizer since it's a simpler `background: var(...)`
 * usage, not stripped).
 *
 * ## Desktop (lg+) layout
 *
 * At lg+ the FAB is hidden via CSS and `isMapSticky` becomes
 * irrelevant — the map lives in a real grid cell with its own
 * `position: sticky; top: 80px` rule defined in `HomeClient.css`.
 * Stale mobile state classes (from a viewport resize) are explicitly
 * reset in the lg+ media block to avoid cross-breakpoint leakage.
 *
 * @param {object} props - Component props.
 * @param {string} [props.initialFaction] - Server-read active faction tab.
 * @param {string} [props.initialRegionsView] - Server-read regions view mode.
 * @param {string} [props.initialSortOrder] - Server-read event-log sort order.
 * @param {import('@/features/stats/StatGrid').PlayersAvg24h | null} [props.playersAvg24h] - 24h player baselines.
 * @param {import('@/features/stats/StatGrid').KillsTrend | null} [props.killsTrend] - 24h/48h kill baselines.
 */
export default function HomeClient(t0) {
  const $ = _c(58);
  const {
    initialFaction: t1,
    initialRegionsView: t2,
    initialSortOrder: t3,
    playersAvg24h: t4,
    killsTrend: t5
  } = t0;
  const initialFaction = t1 === undefined ? "global" : t1;
  const initialRegionsView = t2 === undefined ? "sector" : t2;
  const initialSortOrder = t3 === undefined ? "desc" : t3;
  const playersAvg24h = t4 === undefined ? null : t4;
  const killsTrend = t5 === undefined ? null : t5;
  const {
    data,
    mapState: liveMapState
  } = useLiveDataContext();
  let t6;
  if ($[0] !== data?.events) {
    t6 = data?.events ?? [];
    $[0] = data?.events;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const events = t6;
  let t7;
  if ($[2] !== events) {
    t7 = computePulseDelays(events);
    $[2] = events;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const pulseDelays = t7;
  const {
    selectedEvent,
    railRef
  } = useScrollEvent(events);
  const nowSeconds = Math.floor(Date.now() / 1000);
  let t8;
  if ($[4] !== data) {
    t8 = waveForecast(data, nowSeconds);
    $[4] = data;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const forecast = t8;
  let t9;
  if ($[6] !== data) {
    t9 = counterattackForecast(data, nowSeconds);
    $[6] = data;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const counter = t9;
  const {
    isMapSticky,
    isAnimating,
    togglePin
  } = useMapPin(false);
  const glassFilter = useHeaderGlassFilter();
  let t10;
  if ($[8] !== data || $[9] !== liveMapState || $[10] !== selectedEvent) {
    t10 = selectedEvent ? computeMapStateAtEvent(selectedEvent, data) : liveMapState;
    $[8] = data;
    $[9] = liveMapState;
    $[10] = selectedEvent;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const mapState = t10;
  let t11;
  if ($[12] !== initialFaction || $[13] !== initialRegionsView || $[14] !== killsTrend || $[15] !== playersAvg24h) {
    t11 = <ComponentErrorBoundary name="Dashboard"><DashboardClient initialFaction={initialFaction} initialRegionsView={initialRegionsView} playersAvg24h={playersAvg24h} killsTrend={killsTrend} /></ComponentErrorBoundary>;
    $[12] = initialFaction;
    $[13] = initialRegionsView;
    $[14] = killsTrend;
    $[15] = playersAvg24h;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== selectedEvent) {
    t12 = selectedEvent ? eventKey(selectedEvent) : null;
    $[17] = selectedEvent;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== data) {
    t13 = buildIntroMarkers(data);
    $[19] = data;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== counter || $[22] !== data?.war_start || $[23] !== forecast) {
    t14 = forecast.mode === "window" || counter.mode === "clock" ? <NextWaveCard forecast={forecast} counter={counter} warStart={data?.war_start ?? null} now={nowSeconds} /> : null;
    $[21] = counter;
    $[22] = data?.war_start;
    $[23] = forecast;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== events || $[26] !== initialSortOrder || $[27] !== railRef || $[28] !== t12 || $[29] !== t13 || $[30] !== t14) {
    t15 = <ComponentErrorBoundary name="Event Log"><EventLog events={events} timeFormat="live" title="Event Log" id="event-log" layout="stack" initialSortOrder={initialSortOrder} selectedEventKey={t12} railRef={railRef} introMarkers={t13} futureSlot={t14} onHoverEvent={undefined} /></ComponentErrorBoundary>;
    $[25] = events;
    $[26] = initialSortOrder;
    $[27] = railRef;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t11 || $[33] !== t15) {
    t16 = <div className="home-sidebar gap-8">{t11}{t15}</div>;
    $[32] = t11;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = isMapSticky && "home-map--sticky";
  const t18 = isAnimating && "home-map--pinning";
  let t19;
  if ($[35] !== t17 || $[36] !== t18) {
    t19 = ["home-map", t17, t18].filter(Boolean);
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const t20 = t19.join(" ");
  let t21;
  if ($[38] !== glassFilter) {
    t21 = {
      backdropFilter: glassFilter,
      WebkitBackdropFilter: glassFilter
    };
    $[38] = glassFilter;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== mapState || $[41] !== pulseDelays) {
    t22 = mapState && <Galaxy mapState={mapState} pulseDelays={pulseDelays} />;
    $[40] = mapState;
    $[41] = pulseDelays;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== t22) {
    t23 = <ComponentErrorBoundary name="Galaxy Map">{t22}</ComponentErrorBoundary>;
    $[43] = t22;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t20 || $[46] !== t21 || $[47] !== t23) {
    t24 = <div className={t20} style={t21}>{t23}</div>;
    $[45] = t20;
    $[46] = t21;
    $[47] = t23;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  const t25 = isMapSticky ? "Unpin map" : "Pin map to top";
  const t26 = isMapSticky ? "Unpin map" : "Pin map to top";
  const t27 = isMapSticky ? "\u2715" : "\uD83D\uDCCC";
  let t28;
  if ($[49] !== t25 || $[50] !== t26 || $[51] !== t27 || $[52] !== togglePin) {
    t28 = <button className="home-map-toggle" onClick={togglePin} aria-label={t25} title={t26} data-umami-event="home-map-toggle">{t27}</button>;
    $[49] = t25;
    $[50] = t26;
    $[51] = t27;
    $[52] = togglePin;
    $[53] = t28;
  } else {
    t28 = $[53];
  }
  let t29;
  if ($[54] !== t16 || $[55] !== t24 || $[56] !== t28) {
    t29 = <div className="home-grid gutters">{t16}{t24}{t28}</div>;
    $[54] = t16;
    $[55] = t24;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  return t29;
}